import { parseDayKey } from "../utils.js";

export const GRID_DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

function cellText(val) {
  return String(val ?? "").replace(/\\n/g, "\n").trim();
}

/** Lunch / Break cells: title only, no tasks. */
export function restSlotPlain(activity = "") {
  const a = String(activity || "").toLowerCase();
  if (/\blunch\b/.test(a)) return "Lunch";
  if (/\bbreak\b/.test(a) && !/breakthrough|breakout/.test(a)) return "Break";
  return "";
}

export function isHomeworkActivity(activity) {
  return /homework/i.test(String(activity || ""));
}

export function activityFamily(activity) {
  const a = String(activity || "").toLowerCase();
  if (/homework|tonight/.test(a)) return null;
  if (/standup|stand[\s-]?up|kickoff|morning brief|huddle/.test(a)) return "standup";
  if (/meditation|self introduction|self-intro/.test(a)) return "meditation";
  if (/\bokr\b|objectives?\s+and\s+key|\bkpi\b/.test(a)) return "okr";
  if (/problem review|problem lab|problem statement|focus statement|persona/.test(a)) return "problem";
  if (/system design|architecture|hld|lld|wireframe|user interface|\bui\b/.test(a)) return "sysdesign";
  if (/learning|concept|subject|college/.test(a)) return "learning";
  if (/refresh|game|challenge|quiz/.test(a)) return "game";
  if (/lunch/.test(a)) return "lunch";
  if (/short break|break|tea|snack|short rest|recharge/.test(a)) return "break";
  if (/dsa|leetcode|coding practice|problem solving/.test(a)) return "coding";
  if (/speak|3c|aptitude|product of the day|presentation/.test(a)) return "speak";
  if (/placement|dream company|interview prep/.test(a)) return "placement";
  if (/dt playbook|project|product development/.test(a)) return "project";
  if (/refresh|game/.test(a)) return "game";
  if (/agent workbench|ai agent builder|sns agent/.test(a)) return "agent";
  if (/mini build|peer review|board update|capability/.test(a)) return "profile";
  if (/weekly review|retro|retrospective|demonstrate/.test(a)) return "close";
  const neat = String(activity || "")
    .replace(/^[\s🚀🔍🧪📚🔧🎯🗣️🎮🔄💻📖🧮🏗️🛠️☕🧘🍽️🎤🤖⚡★☆•\-–—]+/u, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .slice(0, 24);
  return neat || "slot";
}

export function neatActivityTitle(raw) {
  const a = String(raw || "").toLowerCase();
  if (/standup|stand[\s-]?up|kickoff|kick[\s-]?off|opening|morning brief/.test(a)) return "Stand-Up";
  if (/lunch/.test(a)) return "Lunch";
  if (/refresh|hollywood|bollywood|pictionary|hot seat|emoji empathy|stack shuffle|two truths|pitch relay|🎮/.test(a)) return "Refresh Game";
  if (/problem review|problem statement review/.test(a)) return "Problem Review";
  if (/product speak|product of the day|🗣️/.test(a)) return "Speak & Solve";
  if (/placement|dream company|target company|🎯/.test(a)) return "Placement Prep";
  if (/weekly review/.test(a)) return "Weekly Review";
  if (/retro|retrospective|reflect/.test(a)) return "Retrospective";
  if (/\bokr\b|okrion/.test(a)) return "OKRion";
  if (/mini build|interest build|side build/.test(a)) return "Mini Build";
  if (/peer review|board update|capability|interests?|goals?|domain/.test(a)) return "Mini Build";
  if (/agent workbench|ai agent builder|sns agent|🤖/.test(a)) return "AI Agent Builder";
  if (/3c|speak\s*&\s*solve|🎤|aptitude/.test(a)) return "Speak & Solve";
  if (/dsa|leetcode|coding practice|🧮|problem solving/.test(a)) return "Coding Practice";
  if (/system design|architecture|🏗️/.test(a)) return "System Design";
  if (/project micro|project push|micro-push/.test(a)) return "Project Push";
  if (/dt playbook|project build|implement|💻|prototype|wireframe|project/.test(a)) return "Project Build";
  if (/learning|📖|📚|concept|subject/.test(a)) return "Learning";
  if (/short break|break|☕|🧘|hydrate/.test(a)) return "Break";
  if (/group|workshop|discussion/.test(a)) return "Group Work";
  return String(raw || "")
    .replace(/^[\s🚀🔍🧪📚🔧🎯🗣️🎮🔄💻📖🧮🏗️🛠️☕🧘🍽️🎤🤖⚡★☆•\-–—]+/u, "")
    .replace(/\s*[—–-]\s*.+$/, "")
    .trim() || "Activity";
}

function toHHMM(chunk) {
  const s = String(chunk || "")
    .replace(/\s*(IST|UTC|GMT|EST|PST|CET|EDT|PDT)\b/gi, " ")
    .trim();
  const ampm = s.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (ampm) {
    let h = Number(ampm[1]);
    const min = ampm[2];
    const ap = ampm[3].toUpperCase();
    if (ap === "AM") {
      if (h === 12) h = 0;
    } else if (h < 12) h += 12;
    return `${String(h).padStart(2, "0")}:${min}`;
  }
  const m = s.match(/(\d{1,2}):(\d{2})/);
  if (!m) return "";
  return `${String(Number(m[1])).padStart(2, "0")}:${m[2]}`;
}

function hhmmTo12(hhmm) {
  const m = String(hhmm || "").match(/^(\d{2}):(\d{2})$/);
  if (!m) return "";
  let h = Number(m[1]);
  const min = m[2];
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${min} ${ap}`;
}

/** Display clocks as 12-hour IST, e.g. 9:00–9:15 AM. Strips UTC/GMT/IST labels. */
export function formatClock(raw) {
  const s = String(raw || "")
    .replace(/\s*(IST|UTC|GMT|EST|PST|CET|EDT|PDT)\b/gi, " ")
    .replace(/\s+/g, " ")
    .replace(/[–—]/g, "-")
    .trim();
  if (!s) return "";
  const range = s.match(/(\d{1,2}:\d{2})\s*(AM|PM)?\s*-\s*(\d{1,2}:\d{2})\s*(AM|PM)?/i);
  if (range) {
    let ap1 = range[2] || "";
    let ap2 = range[4] || "";
    if (!ap1 && ap2) ap1 = ap2;
    if (ap1 && !ap2) ap2 = ap1;
    const a = toHHMM(`${range[1]} ${ap1}`.trim());
    const b = toHHMM(`${range[3]} ${ap2}`.trim());
    if (a && b) {
      const A = hhmmTo12(a);
      const B = hhmmTo12(b);
      if (A.slice(-2) === B.slice(-2)) return `${A.slice(0, -3)}–${B}`;
      return `${A}–${B}`;
    }
  }
  const one = toHHMM(s);
  return one ? hhmmTo12(one) : s;
}

/** Families that should never spawn a 2nd/3rd column at the end of the day. */
const SINGLE_COLUMN_FAMILIES = new Set([
  "close", "profile", "game", "agent", "placement", "speak",
  "coding", "lunch", "standup", "problem", "sysdesign", "project",
]);
const MAX_FAMILY = { learning: 2, break: 1 };

function slotIdForRow(row, familyCounts) {
  const fam = activityFamily(row[2]);
  if (!fam) return null;
  const cap = SINGLE_COLUMN_FAMILIES.has(fam) ? 1 : (MAX_FAMILY[fam] || 2);
  const next = Math.min((familyCounts[fam] || 0) + 1, cap);
  familyCounts[fam] = next;
  return `${fam}#${next}`;
}

export function groupPlanByWeekDay(planRows = []) {
  const byWeekDay = {};
  let autoWeek = 1;
  let lastDayIdx = -1;
  (planRows || []).forEach((row) => {
    if (!Array.isArray(row)) return;
    const parsed = parseDayKey(row[0]);
    if (!parsed) return;
    let { week, dayName } = parsed;
    if (week === null) {
      const dIdx = GRID_DAY_ORDER.indexOf(dayName);
      if (dIdx !== -1 && dIdx <= lastDayIdx) autoWeek++;
      lastDayIdx = dIdx !== -1 ? dIdx : lastDayIdx;
      week = autoWeek;
    }
    if (!byWeekDay[week]) byWeekDay[week] = {};
    if (!byWeekDay[week][dayName]) byWeekDay[week][dayName] = [];
    byWeekDay[week][dayName].push(row);
  });
  return byWeekDay;
}

function hhmmToMinutes(hhmm) {
  const m = String(hhmm || "").match(/^(\d{2}):(\d{2})$/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

function startMinFromClock(raw) {
  const s = String(raw || "")
    .replace(/\s*(IST|UTC|GMT)\b/gi, " ")
    .replace(/[–—]/g, "-")
    .trim();
  const range = s.match(/(\d{1,2}:\d{2})\s*(AM|PM)?\s*-?\s*(\d{1,2}:\d{2})?\s*(AM|PM)?/i);
  if (range) {
    let ap = range[2] || range[4] || "";
    return hhmmToMinutes(toHHMM(`${range[1]} ${ap}`.trim()));
  }
  return hhmmToMinutes(toHHMM(s));
}

function normTitle(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function clockOfSlot(slot = {}) {
  return formatClock(slot.clock || `${slot.start || ""}-${slot.end || ""}`);
}

function guessContentFamily(content = "") {
  const c = String(content || "").toLowerCase();
  if (!c.trim()) return "";
  if (/what agent to build|sns agent workbench|n8n-style|agent to build/.test(c)) return "agent";
  if (/▶\s*done:/.test(c) && /stuck/.test(c)) return "standup";
  if (/short rest|hydrate|stretch/.test(c) && c.length < 220) return /lunch/.test(c) ? "lunch" : "break";
  if (/▶\s*concept:/.test(c)) return "learning";
  if (/real project task|dt playbook/.test(c) || (/▶\s*stage:/.test(c) && /▶\s*step:/.test(c))) return "project";
  if (/system design|hld|lld/.test(c)) return "sysdesign";
  if (/leetcode|dsa\b|coding practice/.test(c)) return "coding";
  if (/3c|speak & solve|product of the day/.test(c)) return "speak";
  return "";
}

function rowMatchScore(row, slot) {
  const t = startMinFromClock(row[1]);
  const a = hhmmToMinutes(slot.start);
  const b = hhmmToMinutes(slot.end);
  let timeScore = 80;
  if (t != null && a != null && b != null) {
    timeScore = t >= a && t < b ? 0 : Math.min(Math.abs(t - a), Math.abs(t - b));
  } else if (t != null && a != null) {
    timeScore = Math.abs(t - a);
  }
  const title = normTitle(row[2]);
  const st = normTitle(slot.title);
  const fam = activityFamily(row[2]);
  const titleHit = Boolean(title && st && (title === st || title.includes(st) || st.includes(title)));
  const famHit = Boolean(fam && (fam === slot.id || activityFamily(slot.title) === fam));
  const titleScore = titleHit ? 0 : famHit ? 8 : 50;
  return timeScore + titleScore;
}

const DEFAULT_ACTIVITY_TITLES = {
  standup: "Stand-Up", problem: "Problem Review", learning: "Learning", break: "Break",
  sysdesign: "System Design", lunch: "Lunch", coding: "Coding Practice", project: "Project Build",
  speak: "Speak & Solve", placement: "Placement Prep", agent: "AI Agent Builder",
  game: "Refresh Game", profile: "Mini Build", close: "Retrospective",
};

function makeFilledSlot(slot, row) {
  const rawTitle = String(row?.[2] || "").trim();
  const slotId = String(slot.id || "").split("#")[0];
  const activity = rawTitle && rawTitle !== DEFAULT_ACTIVITY_TITLES[slotId]
    ? rawTitle
    : String(slot.title || rawTitle || "");
  return {
    id: `${slot.id}#1`,
    row,
    time: clockOfSlot(slot),
    activity,
    content: cellText(row[3]),
  };
}

/** One row → one column. Time + title must agree; leftover Agent/Stand-Up text fills the empty matching column. */
function fillDayAgainstBlueprint(dayRows, slots) {
  const rows = (dayRows || []).filter((r) => Array.isArray(r) && !isHomeworkActivity(r[2]));
  const used = new Set();
  const taken = new Set();
  const byId = {};

  slots.forEach((slot) => {
    let best = -1;
    let bestScore = 1e9;
    rows.forEach((row, i) => {
      if (used.has(i)) return;
      const score = rowMatchScore(row, slot);
      if (score < bestScore) {
        bestScore = score;
        best = i;
      }
    });
    if (best >= 0 && bestScore <= 36) {
      used.add(best);
      taken.add(slot.id);
      byId[`${slot.id}#1`] = makeFilledSlot(slot, rows[best]);
    }
  });

  rows.forEach((row, i) => {
    if (used.has(i)) return;
    const fam = guessContentFamily(row[3]) || activityFamily(row[2]);
    const slot = slots.find((s) => s.id === fam && !taken.has(s.id));
    if (!slot) return;
    used.add(i);
    taken.add(slot.id);
    byId[`${slot.id}#1`] = makeFilledSlot(slot, row);
  });

  const leftovers = rows.filter((_, i) => !used.has(i));
  return { byId, leftovers };
}

function taggedDayRows(dayRows = []) {
  const counts = {};
  return (dayRows || [])
    .map((row) => {
      const id = slotIdForRow(row, counts);
      if (!id) return null;
      return { id, row, time: formatClock(row[1]), activity: neatActivityTitle(row[2]), content: cellText(row[3]) };
    })
    .filter(Boolean);
}

function mergeSlot(into, extra) {
  if (!into) return extra;
  const sameAct = into.activity === extra.activity;
  return {
    ...into,
    content: [into.content, sameAct ? extra.content : `${extra.activity}\n${extra.content}`]
      .filter(Boolean)
      .join("\n\n"),
  };
}

/** One column per period. Each day cell is the single matching row — same data the day list uses. */
export function buildWeekGrid(daysInWeek = {}, blueprint = []) {
  const slots = (blueprint || []).filter((s) => s && s.id);
  if (slots.length) {
    const columns = slots.map((s) => {
      const time = clockOfSlot(s);
      const activity = s.title || s.id;
      return {
        id: `${s.id}#1`,
        time,
        activity,
        header: time ? `${time}\n${activity}` : activity,
        notes: s.notes || "",
        added: Boolean(s.added),
      };
    });
    const extraColumns = [];
    const days = GRID_DAY_ORDER.filter((d) => (daysInWeek[d] || []).length).map((dayName, di) => {
      const { byId, leftovers } = fillDayAgainstBlueprint(daysInWeek[dayName], slots);
      const seen = new Set(Object.values(byId).map((s) => String(s.content || "").slice(0, 140)));
      leftovers.forEach((row) => {
        const content = cellText(row[3]);
        const sig = content.slice(0, 140);
        if (!content || seen.has(sig)) return;
        seen.add(sig);
        const id = `extra#${extraColumns.length + 1}`;
        const activity = String(row[2] || "Activity");
        if (!extraColumns.some((c) => c.id === id)) {
          extraColumns.push({
            id,
            time: formatClock(row[1]),
            activity,
            header: `${formatClock(row[1])}\n${activity}`,
          });
        }
        byId[id] = {
          id,
          row,
          time: formatClock(row[1]),
          activity,
          content,
        };
      });
      return { dayName, dayIndex: di, byId };
    });
    columns.forEach((col) => {
      if (!col.added) return;
      const rest = restSlotPlain(col.activity) || col.activity || "Break";
      days.forEach((d) => {
        if (d.byId[col.id]) return;
        d.byId[col.id] = {
          id: col.id,
          row: null,
          time: col.time,
          activity: col.activity,
          content: rest,
        };
      });
    });
    return { columns: [...columns, ...extraColumns], days };
  }

  return buildWeekGridInferred(daysInWeek);
}

function buildWeekGridInferred(daysInWeek = {}) {
  const taggedByDay = {};
  const columnOrder = [];
  GRID_DAY_ORDER.forEach((day) => {
    taggedByDay[day] = taggedDayRows(daysInWeek[day]);
    taggedByDay[day].forEach((slot) => {
      if (!columnOrder.includes(slot.id)) columnOrder.push(slot.id);
    });
  });

  const activeDays = GRID_DAY_ORDER.filter((d) => taggedByDay[d].length);
  const nDays = activeDays.length || 1;
  const minCoverage = nDays === 1 ? 1 : Math.max(2, Math.ceil(nDays / 2));
  const coverage = {};
  activeDays.forEach((d) => {
    const seen = new Set();
    taggedByDay[d].forEach((s) => {
      if (seen.has(s.id)) return;
      seen.add(s.id);
      coverage[s.id] = (coverage[s.id] || 0) + 1;
    });
  });

  const keptIds = columnOrder.filter((id) => (coverage[id] || 0) >= minCoverage);
  const familyKept = {};
  keptIds.forEach((id) => {
    familyKept[id.split("#")[0]] = id;
  });

  const remapId = (id) => {
    if (keptIds.includes(id)) return id;
    const fam = id.split("#")[0];
    return familyKept[fam] || id;
  };

  const columns = keptIds.map((id) => {
    const canonical = GRID_DAY_ORDER.flatMap((d) => taggedByDay[d].filter((s) => s.id === id));
    const samples = GRID_DAY_ORDER.flatMap((d) => taggedByDay[d].filter((s) => remapId(s.id) === id));
    const timeVotes = {};
    canonical.forEach((s) => {
      timeVotes[s.time] = (timeVotes[s.time] || 0) + 1;
    });
    const time =
      Object.entries(timeVotes).sort((a, b) => b[1] - a[1])[0]?.[0] ||
      canonical[0]?.time ||
      samples[0]?.time ||
      "";
    const activity = canonical[0]?.activity || samples[0]?.activity || "";
    return { id, time, activity, header: time ? `${time}\n${activity}` : activity };
  });

  const days = GRID_DAY_ORDER.filter((d) => (daysInWeek[d] || []).length).map((dayName, di) => {
    const byId = {};
    taggedByDay[dayName].forEach((s) => {
      const id = remapId(s.id);
      byId[id] = mergeSlot(byId[id], { ...s, id });
    });
    return { dayName, dayIndex: di, byId };
  });

  return { columns, days };
}

/** Day list from saved plan rows — original title + content, time-sorted, no remap. */
export function dayListRowsFromPlan(dayRows = []) {
  const rows = (dayRows || []).filter((r) => Array.isArray(r));
  const seen = new Set();
  return rows
    .map((r, origIndex) => ({ r, origIndex }))
    .filter(({ r }) => !isHomeworkActivity(r[2]))
    .sort((a, b) => (startMinFromClock(a.r[1]) ?? 9999) - (startMinFromClock(b.r[1]) ?? 9999))
    .map(({ r, origIndex }) => {
      const content = cellText(r[3]);
      const title = String(r[2] || "").replace(/^[\s🚀🔍🧪📚🔧🎯🗣️🎮🔄💻📖🧮🏗️🛠️☕🧘🍽️🎤🤖⚡★☆•]+/u, "").trim();
      const clock = formatClock(r[1]);
      const fam = activityFamily(title) || content.slice(0, 40);
      const sig = `${clock}|${fam}`;
      if (seen.has(sig)) return null;
      if (!content && !title) return null;
      seen.add(sig);
      return [r[0], clock, title || "Activity", r[3], origIndex];
    })
    .filter(Boolean);
}

/** Flatten one week-table day into day-list rows (same cells, list format). */
export function weekDayToListRows(weekGrid, dayName, fallbackDayKey = "") {
  const day = (weekGrid?.days || []).find((d) => d.dayName === dayName);
  if (!day) return [];
  return (weekGrid.columns || [])
    .map((col) => {
      const slot = day.byId?.[col.id];
      if (!slot) return null;
      const row = Array.isArray(slot.row) ? slot.row : [];
      const content = slot.content != null && slot.content !== ""
        ? slot.content
        : cellText(row[3]);
      if (!String(content || "").trim() && !slot.activity) return null;
      return [
        row[0] || fallbackDayKey,
        col.time || slot.time || formatClock(row[1]),
        col.activity || slot.activity || neatActivityTitle(row[2]),
        content,
      ];
    })
    .filter(Boolean);
}

/** Day list: same order/titles as the activity timetable. */
export function alignDayListRows(dayRows = [], blueprint = []) {
  const rows = (dayRows || []).filter((r) => Array.isArray(r));
  const hw = rows.filter((r) => isHomeworkActivity(r[2]));
  const work = rows.filter((r) => !isHomeworkActivity(r[2]));
  const slots = (blueprint || []).filter((s) => s && s.id);
  if (!slots.length) {
    return [...work]
      .sort((a, b) => (startMinFromClock(a[1]) ?? 9999) - (startMinFromClock(b[1]) ?? 9999))
      .concat(hw);
  }
  const { byId, leftovers } = fillDayAgainstBlueprint(work, slots);
  const out = slots
    .map((slot) => {
      const filled = byId[`${slot.id}#1`];
      if (!filled) return null;
      return [filled.row?.[0] || work[0]?.[0], filled.time, filled.activity, filled.content];
    })
    .filter(Boolean);
  leftovers.forEach((r) => {
    if (!String(r[3] || "").trim()) return;
    out.push([r[0], formatClock(r[1]), neatActivityTitle(r[2]), r[3]]);
  });
  return out.concat(hw);
}

export function dayGridLabel(week, dayName) {
  const di = GRID_DAY_ORDER.indexOf(dayName);
  const dayNum = (Number(week) - 1) * 5 + (di >= 0 ? di : 0) + 1;
  return `Day ${dayNum} (W${week}D${di + 1})`;
}

export function weekGridToAoa(week, daysInWeek, extraDayMeta = {}, blueprint = []) {
  const { columns, days } = buildWeekGrid(daysInWeek, blueprint);
  const header = ["Day", ...columns.map((c) => c.header)];
  const body = days.map((d) => {
    const meta = extraDayMeta[d.dayName] || {};
    const label = [dayGridLabel(week, d.dayName), meta.date].filter(Boolean).join("\n");
    return [
      label,
      ...columns.map((c) => {
        const slot = d.byId[c.id];
        if (!slot) return "";
        const bits = [slot.activity, slot.content].filter(Boolean);
        return bits.join("\n");
      }),
    ];
  });
  return { header, body, columns };
}

export function dailyPlanToWeekSheets(planRows = [], calMap = null, getDateFn = null, blueprint = []) {
  const byWeekDay = groupPlanByWeekDay(planRows);
  const weeks = Object.keys(byWeekDay).map(Number).sort((a, b) => a - b);
  return weeks.map((week) => {
    const extra = {};
    GRID_DAY_ORDER.forEach((day) => {
      const date = getDateFn && calMap ? getDateFn(calMap, week, day) : null;
      if (date) extra[day] = { date };
    });
    const { header, body, columns } = weekGridToAoa(week, byWeekDay[week] || {}, extra, blueprint);
    return { week, sheetName: `Week ${week}`.slice(0, 31), header, body, columns, kind: "weekGrid" };
  });
}

export function dailyPlanToDayListSheets(planRows = [], preserveActivityTitles = false) {
  const byWeekDay = groupPlanByWeekDay(planRows);
  const weeks = Object.keys(byWeekDay).map(Number).sort((a, b) => a - b);
  const sheets = [];
  weeks.forEach((week) => {
    GRID_DAY_ORDER.forEach((day) => {
      const rows = (byWeekDay[week]?.[day] || []).filter((r) => !isHomeworkActivity(r[2]));
      if (!rows.length) return;
      sheets.push({
        week,
        sheetName: `W${week}-${day.slice(0, 3)}`.slice(0, 31),
        header: ["Time (IST)", "Activity", "Task / Content"],
        body: rows.map((r) => [String(r[1] || ""), preserveActivityTitles ? String(r[2] || "") : neatActivityTitle(r[2]), cellText(r[3])]),
        kind: "dayList",
      });
    });
  });
  return sheets;
}

export function dailySheetsForLayout(layout, planRows = [], calMap = null, getDateFn = null, blueprint = [], preserveActivityTitles = false) {
  if (layout === "dayList") return dailyPlanToDayListSheets(planRows, preserveActivityTitles);
  return dailyPlanToWeekSheets(planRows, calMap, getDateFn, blueprint);
}

export function applyDayListSheetLayout(ws, XLSX, rowCount) {
  ws["!cols"] = [{ wch: 18 }, { wch: 28 }, { wch: 100 }];
  ws["!rows"] = Array.from({ length: rowCount }, (_, i) => ({ hpt: i === 0 ? 22 : 75 }));
  if (!ws["!ref"]) return;
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      if (!ws[addr]) continue;
      ws[addr].s = {
        alignment: { wrapText: true, vertical: C === 0 ? "center" : "top" },
        font: R === 0 ? { bold: true, color: { rgb: "FFFFFF" } } : { sz: 10 },
        fill: R === 0 ? { fgColor: { rgb: "1A2233" } } : undefined,
      };
    }
  }
}

export function appendDailySheetsToWorkbook(wb, XLSX, sheets) {
  (sheets || []).forEach(({ sheetName, header, body, kind }) => {
    const aoa = [header, ...body];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    if (kind === "dayList") applyDayListSheetLayout(ws, XLSX, aoa.length);
    else applyGridSheetLayout(ws, XLSX, aoa.length, header.length);
    XLSX.utils.book_append_sheet(wb, ws, String(sheetName || "Plan").slice(0, 31));
  });
}

export function applyGridSheetLayout(ws, XLSX, rowCount, colCount) {
  ws["!cols"] = [{ wch: 22 }, ...Array.from({ length: Math.max(0, colCount - 1) }, () => ({ wch: 36 }))];
  ws["!rows"] = Array.from({ length: rowCount }, (_, i) => ({ hpt: i === 0 ? 36 : 96 }));
  if (!ws["!ref"]) return;
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      if (!ws[addr]) continue;
      ws[addr].s = {
        alignment: { wrapText: true, vertical: "top", horizontal: C === 0 || R === 0 ? "center" : "left" },
        font: R === 0 ? { bold: true, color: { rgb: "FFFFFF" } } : { sz: 10 },
        fill: R === 0 ? { fgColor: { rgb: "1B4D5C" } } : undefined,
      };
    }
  }
}
